import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { useReportDrivingStore } from "../../../store/reports/reportDrivingStore";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { ReportStatCard, ReportChart, tickColor, gridColor } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { toLocalInputValue, makeDateTimeFormatter, resolveTz } from "../utils/reportDateFormat";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import type { ChartConfiguration } from "chart.js";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";

const MAX_RANGE_DAYS = 31;

interface DrivingFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
  maxSpeed: string;
  rpm: string;
}

interface DrivingRowUI {
  id: string;
  unidad: string;
  fecha: string;
  hora: string;
  estadoMotor: string;
  velocidad: string;
  coordenadas: string;
  evento: string;
}

const DRIVING_COLUMNS: ReportTableColumn<DrivingRowUI>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "fecha", header: "Fecha" },
  { key: "hora", header: "Hora" },
  { key: "estadoMotor", header: "Estado de motor" },
  { key: "velocidad", header: "Velocidad", align: "right" },
  { key: "coordenadas", header: "Coordenadas" },
  { key: "evento", header: "Evento" },
];

// Mismo formato que el resumen del PDF/Excel (ReportUtils.formatDuration).
const fmtDuration = (seconds: number): string => {
  if (!seconds || seconds < 0) return "00hrs 00min 00seg";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(seconds / 3600))}hrs ${pad(Math.floor((seconds % 3600) / 60))}min ${pad(Math.floor(seconds % 60))}seg`;
};

export default function DrivingSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const loadRange = useReportDrivingStore((state) => state.loadRange);
  const clearDriving = useReportDrivingStore((state) => state.clear);
  const resultsState = useReportDrivingStore((state) => state.results);
  const order = useReportDrivingStore((state) => state.order);
  const drivingLoading = useReportDrivingStore((state) => state.loading);
  const tz = useReportDrivingStore((state) => state.tz);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<DrivingFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "driving",
      typeDocument: "pdf",
      maxSpeed: "",
      rpm: "",
    },
  });

  const { showAlert } = useAlert();
  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => {
    clearDriving();
    setLoadAttempted(false);
  });
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const hasDevices = (selectedDevices?.length ?? 0) > 0;
  const hasDates = Boolean(startDate && endDate);
  const rangeDays = useMemo(() => {
    if (!hasDates) return 0;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
  }, [endDate, hasDates, startDate]);
  const hasValidRange = hasDates && rangeDays >= 0;
  const isWithinViewLimit = hasValidRange && rangeDays <= MAX_RANGE_DAYS;
  const filtersReadyForView = hasDevices && hasValidRange && isWithinViewLimit;

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: DrivingFormData) => {
    const result = await generateHistoryReport(
      {
        deviceIds: formData.devices,
        startDate: formData.startDate,
        endDate: formData.endDate,
        typeReport: formData.typeReport,
        typeDocument: formData.typeDocument,
        maxSpeed: formData.maxSpeed,
        rpm: formData.rpm,
      },
      showAlert,
      () => {},
    );

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type:
          formData.typeDocument === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const resolveDeviceById = useCallback((): Record<string, { name: string; brandName: string | null; brandModel: string | null }> => {
    const map: Record<string, { name: string; brandName: string | null; brandModel: string | null }> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = {
        name: d.vehicle?.name || d.identifier || d.id,
        brandName: d.brand?.brand ?? null,
        brandModel: d.brand?.model ?? null,
      };
    });
    return map;
  }, [allDevices]);

  const onViewDriving = async () => {
    const { devices, startDate, endDate, maxSpeed, rpm } = getValues();

    if (!devices || devices.length === 0) {
      showAlert({
        title: "Selecciona unidades",
        text: "Selecciona al menos un equipo para ver la conducción.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    if (!startDate || !endDate) {
      showAlert({
        title: "Fechas requeridas",
        text: "Selecciona la fecha de inicio y fin.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
    if (days > MAX_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver conducción solo muestra hasta ${MAX_RANGE_DAYS} días. Para rangos mayores usa "Generar Reporte".`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const fromIso = new Date(startDate).toISOString();
    const toIso = new Date(endDate).toISOString();
    const byId = resolveDeviceById();
    const devicesToLoad = devices.map((id) => ({
      deviceId: id,
      vehicleName: byId[id]?.name || id,
      brandName: byId[id]?.brandName ?? null,
      brandModel: byId[id]?.brandModel ?? null,
    }));

    await loadRange(devicesToLoad, fromIso, toIso, {
      maxSpeed: parseFloat(maxSpeed) || 120,
      maxRpm: parseFloat(rpm) || 2500,
    });
    setLoadAttempted(true);
  };

  const orderedResults = useMemo(
    () => order.map((id) => resultsState[id]).filter(Boolean),
    [order, resultsState],
  );

  const totals = useMemo(() => {
    let totalPoints = 0;
    let idleCount = 0;
    let totalIdleTime = 0;
    orderedResults.forEach((r) => {
      totalPoints += r.totalPoints;
      idleCount += r.idleCount;
      totalIdleTime += r.totalIdleTime;
    });
    return { totalPoints, idleCount, totalIdleTime };
  }, [orderedResults]);

  const fmtDateTime = useMemo(() => makeDateTimeFormatter(resolveTz(tz), true), [tz]);

  const tableRows = useMemo<DrivingRowUI[]>(
    () =>
      orderedResults.flatMap((r) =>
        r.rows.map((row, i) => {
          const [fecha, hora] = fmtDateTime(row.ts).split(" ");
          return {
            id: `${r.deviceId}-${row.ts}-${i}`,
            unidad: r.vehicleName,
            fecha,
            hora,
            estadoMotor: row.estadoMotor,
            velocidad: row.velocidad == null ? "N/A" : `${row.velocidad.toFixed(0)} km/h`,
            coordenadas: `${row.lat.toFixed(6)}, ${row.lon.toFixed(6)}`,
            evento: row.evento,
          };
        }),
      ),
    [orderedResults, fmtDateTime],
  );

  const chartConfig = useMemo<ChartConfiguration | null>(() => {
    const withData = orderedResults.filter((r) => r.totalPoints > 0);
    if (withData.length === 0) return null;
    return {
      type: "bar",
      data: {
        labels: withData.map((r) => r.vehicleName),
        datasets: [
          {
            label: "Eventos de ralentí",
            data: withData.map((r) => r.idleCount),
            backgroundColor: withData.map((r) => r.color),
            borderRadius: 4,
            maxBarThickness: 26,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { displayColors: false } },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { color: tickColor(isDarkMode), precision: 0 },
            grid: { color: gridColor(isDarkMode) },
          },
          y: {
            ticks: { color: tickColor(isDarkMode), autoSkip: false, font: { size: 11 } },
            grid: { display: false },
          },
        },
      },
    };
  }, [orderedResults, isDarkMode]);

  const drivingChartHeight = Math.max(220, orderedResults.filter((r) => r.totalPoints > 0).length * 40);

  const onDownload = async (typeDocument: "pdf" | "excel") => {
    if (tableRows.length === 0) return;
    const { startDate, endDate } = getValues();
    const extension = typeDocument === "pdf" ? "pdf" : "xlsx";
    const fileName = `driving_${startDate.split("T")[0]}_${endDate.split("T")[0]}.${extension}`;
    const period =
      `Período: ${startDate.replace("T", " ")} al ${endDate.replace("T", " ")}` +
      ` | Ralentí: ${totals.idleCount} (${fmtDuration(totals.totalIdleTime)})`;

    setExporting(typeDocument);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: "Informe de Conducción", period, fileName };
      if (typeDocument === "excel") {
        await exportHistoricToExcel(tableRows, DRIVING_COLUMNS, meta);
      } else {
        exportHistoricToPdf(tableRows, DRIVING_COLUMNS, meta);
      }
    } catch {
      showAlert({
        title: "Error al descargar",
        text: "No se pudo generar el archivo de conducción.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    setValue("startDate", toLocalInputValue(new Date()));
    setValue("endDate", toLocalInputValue(new Date(new Date().setDate(new Date().getDate() + 15))));
  }, []);

  useEffect(() => {
    return () => {
      clearDriving();
      setLoadAttempted(false);
    };
  }, [clearDriving]);

  const hasResults = orderedResults.length > 0;
  const showSkeleton = drivingLoading;
  const showEmptyState = !hasResults && !showSkeleton;

  const summaryCards = [
    { label: "Puntos analizados", value: String(totals.totalPoints), icon: "ri-focus-3-line" },
    { label: "Eventos de ralentí", value: String(totals.idleCount), icon: "ri-pause-circle-line" },
    { label: "Tiempo en ralentí", value: fmtDuration(totals.totalIdleTime), icon: "ri-time-line" },
    { label: "Unidades", value: String(orderedResults.length), icon: "ri-truck-line" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="maxSpeed" className={`${themeClasses.text} text-xs`}>Velocidad máxima (km/h)</label>
            <div className="flex flex-col gap-1">
              <input
                {...register("maxSpeed")}
                className={`p-1 rounded-lg ${themeClasses.inputBg} ${themeClasses.inputBorder} ${themeClasses.inputText} ${themeClasses.inputPlaceholder} border-2`} placeholder="120"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="rpm" className={`${themeClasses.text} text-xs`}>Revoluciones máx (rpm)</label>
            <div className="flex flex-col gap-1">
              <input
                {...register("rpm")}
                className={`p-1 rounded-lg ${themeClasses.inputBg} ${themeClasses.inputBorder} ${themeClasses.inputText} ${themeClasses.inputPlaceholder} border-2`} placeholder="2500"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Formato</label>
            <Controller
              name="typeDocument"
              control={control}
              render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton
            type="button"
            variant="secondary"
            onClick={onViewDriving}
            disabled={drivingLoading || !filtersReadyForView || disableWithoutCompany}
            title={!filtersReadyForView && hasDevices && hasValidRange && !isWithinViewLimit ? `Ver conducción solo admite hasta ${MAX_RANGE_DAYS} días` : undefined}
          >
            Ver conducción
          </ReportActionButton>
          <ReportActionButton
            type="submit"
            disabled={loading || !hasDevices || !hasValidRange || disableWithoutCompany}
          >
            {loading ? "Generando..." : "Generar Reporte"}
          </ReportActionButton>
        </div>
      </form>

      {showSkeleton && (
        <ReportLoadingPanel
          isDarkMode={isDarkMode}
          icon="ri-steering-2-line"
          title="Analizando conducción…"
          subtitle="Obteniendo posiciones del rango seleccionado"
        />
      )}

      {hasResults && !showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {orderedResults.length} unidad(es) · {totals.totalPoints} punto(s)
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onDownload("pdf")}
                disabled={exporting !== null || tableRows.length === 0}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "pdf" ? "Generando..." : "Descargar PDF"}
              </button>
              <button
                type="button"
                onClick={() => onDownload("excel")}
                disabled={exporting !== null || tableRows.length === 0}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "excel" ? "Generando..." : "Descargar Excel"}
              </button>
              <button
                type="button"
                onClick={() => {
                  clearDriving();
                  setLoadAttempted(false);
                }}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {summaryCards.map((card) => (
              <ReportStatCard key={card.label} icon={card.icon} label={card.label} value={card.value} isDarkMode={isDarkMode} />
            ))}
          </div>

          {chartConfig && (
            <div className={`sticky top-0 z-20 p-3 rounded-xl border shadow-sm ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"}`}>
              <p className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>Eventos de ralentí por unidad</p>
              <ReportChart config={chartConfig} height={drivingChartHeight} />
            </div>
          )}

          <ReportPointsTable
            data={tableRows}
            columns={DRIVING_COLUMNS}
            isDarkMode={isDarkMode}
            emptyText="No se encontraron datos de conducción en el rango seleccionado."
          />
        </div>
      )}

      {showEmptyState && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`relative w-full overflow-hidden rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`} style={{ minHeight: 220 }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
                <i className="ri-steering-2-line text-primary-500 text-2xl" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${themeClasses.text}`}>Informe de conducción</p>
                <p className={`text-xs mt-1 max-w-md ${themeClasses.textSecondary}`}>
                  Configura los parámetros, equipos y fechas, y usa <strong>Ver conducción</strong> (hasta {MAX_RANGE_DAYS} días) para ver el detalle y el ralentí. Para rangos mayores usa <strong>Generar Reporte</strong>.
                </p>
              </div>
            </div>
          </div>

          {loadAttempted && !drivingLoading && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-100 border-gray-200"}`}>
              <div className="flex items-start space-x-2">
                <i className={`ri-information-line text-base ${themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${themeClasses.textSecondary}`}>Sin resultados</p>
                  <p className={`text-sm ${themeClasses.text} mt-0.5`}>
                    No se encontraron datos de conducción en el rango seleccionado. Verifica equipos y fechas, o usa <strong>Generar Reporte</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
